import { createFileRoute, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";

import { getBokunStatus } from "@/lib/bokun-status.functions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const statusQuery = queryOptions({
  queryKey: ["bokun", "status"],
  queryFn: () => getBokunStatus(),
  staleTime: 0,
});

export const Route = createFileRoute("/_authenticated/admin/bokun-status")({
  head: () => ({ meta: [{ title: "Bokun integration status" }, { name: "robots", content: "noindex,nofollow" }] }),
  loader: ({ context }) => context.queryClient.ensureQueryData(statusQuery),
  component: StatusPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-destructive">{error instanceof Error ? error.message : String(error)}</div>
  ),
});

function StatusPage() {
  const router = useRouter();
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Bokun integration status</h1>
            <p className="text-sm text-muted-foreground">Live diagnostic of the API connection</p>
          </div>
          <Button variant="outline" onClick={() => router.invalidate()}>Re-test</Button>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 py-8">
        <Suspense fallback={<Skeleton className="h-96 w-full" />}>
          <StatusBody />
        </Suspense>
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b py-3 last:border-0 sm:flex-row sm:items-start sm:justify-between">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="break-all text-sm sm:text-right">{value}</span>
    </div>
  );
}

function StatusBody() {
  const { data: s } = useSuspenseQuery(statusQuery);
  const okBadge = s.ok ? (
    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Connected</Badge>
  ) : (
    <Badge variant="destructive">Failing</Badge>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Connection {okBadge}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Row label="API flavor" value={s.apiFlavor} />
          <Row label="Auth method" value="HMAC-SHA1 (X-Bokun-AccessKey + X-Bokun-Signature)" />
          <Row label="Base URL (BOKUN_API_URL)" value={<code>{s.baseUrl}</code>} />
          <Row label="Products endpoint" value={<code>POST {s.productsEndpoint}</code>} />
          <Row
            label="Access key"
            value={s.accessKeyConfigured ? `configured (${s.accessKeyLength} chars)` : "missing"}
          />
          <Row label="Secret key" value={s.secretKeyConfigured ? "configured" : "missing"} />
          <Row
            label="HTTP response"
            value={s.httpStatus !== null ? `${s.httpStatus} ${s.httpStatusText ?? ""}` : "—"}
          />
          <Row label="Products found" value={s.productCount !== null ? s.productCount : "—"} />
          <Row label="Last sync" value={new Date(s.lastSyncIso).toLocaleString()} />
          <Row label="Round-trip time" value={`${s.elapsedMs} ms`} />
        </CardContent>
      </Card>

      {s.authError && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Error</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>{s.authError}</p>
            {s.hint && (
              <p className="rounded-md bg-muted p-3 text-muted-foreground">
                <strong>Hint:</strong> {s.hint}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Raw response sample (first 800 chars)</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
            {s.sampleRaw ?? "—"}
          </pre>
        </CardContent>
      </Card>

      {s.sampleParsed != null && (
        <Card>
          <CardHeader>
            <CardTitle>First product (truncated)</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
              {s.sampleParsed}
            </pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Scope reminder</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            This integration uses the <strong>REST Legacy v1</strong> API (HMAC-signed). It does
            <em> not</em> use GraphQL. For it to work, the access key in Bokun must have the
            <code className="mx-1 rounded bg-muted px-1">LEGACY_API</code> scope enabled.
          </p>
          <p>
            GraphQL would require a Bearer access token with scopes like
            <code className="mx-1 rounded bg-muted px-1">PRODUCTS_READ</code> — not used here.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
